import { graphlib } from "@dagrejs/dagre";
import type { ParsedRuleGraph, RawRuleGraph, RuleGraph } from "./GraphTypes";
import pako from "pako";

export type StoredRuleGraph = RuleGraph | graphlib.Graph | Uint8Array | string;

export const isParsedRuleGraph = (graph: any): graph is ParsedRuleGraph => {
  if (typeof graph !== "object" || graph === null) {
    return false;
  }
  return "node" in graph && typeof graph.node === "function";
};

export const isRawRuleGraph = (graph: StoredRuleGraph): graph is RawRuleGraph => {
  return typeof graph === "object" &&
    // @ts-ignore
    graph !== null && Array.isArray(graph.nodes) && Array.isArray(graph.edges);
};

// same as graphlib.json.read except it does for, of instead of forEach because it's faster
const jsonToGraph = (json: any) => {
  const g = new graphlib.Graph(json.options).setGraph(json.value);
  for (const entry of json.nodes) {
      g.setNode(entry.v, entry.value);
      if (entry.parent) {
        g.setParent(entry.v, entry.parent);
      }
    }
  for (const entry of json.edges) {
      g.setEdge({ v: entry.v, w: entry.w, name: entry.name }, entry.value);
    }
  return g;
}

const PARSED_SYMBOL = Symbol("parsed");

/**
 * Given a graph in any storage format it will ensure the output is a dagrejs/graphlib graph.
 * - Will throw if decompression or JSON Parsing fails.
 * @param graph Decisively graph in any format: compressed, JSON, dagrejs/graphlib.
 * @returns graph in dagrejs/graphlib object format.
 */
export const ensureParsedGraph = (graph: StoredRuleGraph & {
  [PARSED_SYMBOL]?: ParsedRuleGraph;
}): ParsedRuleGraph => {
  if (graph[PARSED_SYMBOL]) {
    return graph[PARSED_SYMBOL];
  }

  // First check if it's a compressed graph (Uint8Array)
  if (graph instanceof Uint8Array || Array.isArray(graph) || (
    typeof graph === "object" &&
    graph !== null &&
    Object.keys(graph).every(key => !isNaN(Number(key))) &&
    Object.values(graph).every(value => typeof value === "number" && value >= 0 && value <= 255)
  )) {
    try {

      // Convert plain object to Uint8Array if needed
      const uint8Array = graph instanceof Uint8Array ? graph : new Uint8Array(Array.isArray(graph) ? graph : Object.values(graph));
      const uncompressed = JSON.parse(pako.inflate(uint8Array, { to: "string" }));
      return graph[PARSED_SYMBOL] = jsonToGraph(uncompressed) as ParsedRuleGraph;
    } catch (error) {
      throw new Error("Failed to uncompress graph");
    }
    // @ts-ignore - one weird tests does this
  } else if (typeof graph === 'object' && graph.type === 'Buffer') {
    try {
      // @ts-ignore - one weird tests does this
      const uint8Array = new Uint8Array(graph.data ?? Object.values(graph));
      return graph[PARSED_SYMBOL] = jsonToGraph(JSON.parse(pako.inflate(uint8Array, { to: "string" }))) as ParsedRuleGraph;
    } catch (error) {
      throw new Error("Failed to uncompress graph");
    }
  }

  if (typeof graph === "string") {
    return graph[PARSED_SYMBOL] = jsonToGraph(JSON.parse(graph)) as ParsedRuleGraph;
  }

  // Then check if it's already a dagrejs graphlib
  if (isParsedRuleGraph(graph)) {
    return graph as ParsedRuleGraph;
  }

  // Check if it looks like a valid graph JSON structure
  if (
    isRawRuleGraph(graph)
  ) {
    try {
      if (Object.isExtensible(graph)) {
        return graph[PARSED_SYMBOL] = jsonToGraph(graph) as ParsedRuleGraph;
      }
      return jsonToGraph(graph) as ParsedRuleGraph;
    } catch (error) {
      // @ts-ignore
      throw new Error("Failed to parse graph JSON", {
        cause: error,
      });
    }
  }

  throw new Error("graphlib format not recognized");
};
